/**
 * FmMenu internal utilities and hooks.
 * Extracted from FmMenu.tsx to keep main component <500 LOC.
 */
import React from 'react';
import type { FmMenuPlacement } from './FmMenu';

const OFFSET = 6;
const VIEWPORT_GUTTER = 8;
const MENU_ITEM_SELECTOR = '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])';

export const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export const menuMinWidth = (value?: number | string): string | undefined => {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

export const isInteractiveKey = (key: string): boolean =>
  key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp';

export const isMenuNavigationKey = (key: string): boolean =>
  key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End';

export function getEnabledItems(root: HTMLElement | null): HTMLButtonElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR));
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function focusFirstItem(menuRef: React.RefObject<HTMLDivElement | null>): void {
  const items = getEnabledItems(menuRef.current);
  items[0]?.focus();
}

export function focusTrigger(triggerRootRef: React.RefObject<HTMLSpanElement | null>): void {
  const button = triggerRootRef.current?.querySelector<HTMLButtonElement>('button');
  button?.focus();
}

export function focusByNavigationKey(
  key: string,
  menuRef: React.RefObject<HTMLDivElement | null>,
): void {
  const items = getEnabledItems(menuRef.current);
  const count = items.length;
  if (count === 0) return;

  const focused = document.activeElement as HTMLElement;
  const focusedIndex = Array.from(items).indexOf(focused as HTMLButtonElement);

  // ArrowUp/ArrowDown wrap around the ends (standard menu roving-focus behavior):
  // ArrowUp from the first item lands on the last, ArrowDown from the last on the
  // first. Home/End jump to the edges.
  let nextIndex = focusedIndex;
  if (key === 'ArrowDown') nextIndex = focusedIndex < 0 ? 0 : (focusedIndex + 1) % count;
  if (key === 'ArrowUp') nextIndex = focusedIndex < 0 ? count - 1 : (focusedIndex - 1 + count) % count;
  if (key === 'Home') nextIndex = 0;
  if (key === 'End') nextIndex = count - 1;

  items[nextIndex]?.focus();
}

export interface UseControllableOpenArgs {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function useControllableOpen({
  open,
  defaultOpen,
  onOpenChange,
}: UseControllableOpenArgs): [boolean, (value: boolean, focus?: boolean) => void, React.RefObject<HTMLElement | null>] {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen || false);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);

  const isOpen = isControlled ? open : uncontrolledOpen;
  // `setOpen` must be referentially stable: `useMenuLifecycle`'s effect lists it
  // in its dependency array. An unstable `setOpen` re-fires that effect on every
  // render, and because the effect calls `updatePosition()` (which commits a new
  // style object), that would drive an unbounded setState-in-useEffect loop
  // ("Maximum update depth exceeded") whenever a menu is open.
  const setOpen = React.useCallback(
    (value: boolean, focus?: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setUncontrolledOpen(value);
      }
      if (focus) {
        restoreFocusRef.current = document.activeElement as HTMLElement;
      }
    },
    [isControlled, onOpenChange],
  );

  return [isOpen, setOpen, restoreFocusRef];
}

export interface UseMenuPositionArgs {
  isOpen: boolean;
  placement: FmMenuPlacement;
  triggerRootRef: React.RefObject<HTMLSpanElement | null>;
  menuRef: React.RefObject<HTMLDivElement | null>;
}

export function useMenuPosition({
  isOpen,
  placement,
  triggerRootRef,
  menuRef,
}: UseMenuPositionArgs): [
  React.CSSProperties,
  () => void,
] {
  const [position, setPosition] = React.useState<React.CSSProperties>({});

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRootRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]');
    const menu = menuRef.current;
    if (!trigger || !menu || !isOpen) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top: number;
    let left: number;

    if (placement === 'bottomLeft' || placement === 'bottomRight') {
      top = triggerRect.bottom + OFFSET;
      if (top + menuRect.height + VIEWPORT_GUTTER > viewportHeight) {
        top = triggerRect.top - menuRect.height - OFFSET;
      }
    } else {
      top = triggerRect.top - menuRect.height - OFFSET;
      if (top - VIEWPORT_GUTTER < 0) {
        top = triggerRect.bottom + OFFSET;
      }
    }

    if (placement === 'bottomLeft' || placement === 'topLeft') {
      left = triggerRect.left;
    } else {
      left = triggerRect.right - menuRect.width;
    }

    left = clamp(left, VIEWPORT_GUTTER, viewportWidth - menuRect.width - VIEWPORT_GUTTER);

    const nextTop = `${top}px`;
    const nextLeft = `${left}px`;
    // Bail out when the recomputed position is identical to the committed one.
    // `updatePosition` runs on every scroll/resize and on effect re-runs; without
    // this guard each call commits a brand-new style object, forcing a re-render
    // even when nothing moved. That makes the hook a re-render amplifier and, if
    // any consumer supplies an unstable dependency, an unbounded update loop.
    setPosition((prev) =>
      prev.position === 'fixed' &&
      prev.top === nextTop &&
      prev.left === nextLeft &&
      prev.zIndex === 1050
        ? prev
        : {
            position: 'fixed',
            top: nextTop,
            left: nextLeft,
            zIndex: 1050,
          },
    );
  }, [isOpen, placement, triggerRootRef, menuRef]);

  React.useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  return [position, updatePosition];
}

export interface UseMenuLifecycleArgs {
  autoFocus: boolean;
  focusFirstOnOpenRef: React.RefObject<boolean>;
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement | null>;
  restoreFocusOnClose: boolean;
  restoreFocusRef: React.RefObject<HTMLElement | null>;
  setOpen: (value: boolean, focus?: boolean) => void;
  triggerRootRef: React.RefObject<HTMLSpanElement | null>;
  updatePosition: () => void;
}

export function useMenuLifecycle({
  autoFocus,
  focusFirstOnOpenRef,
  isOpen,
  menuRef,
  restoreFocusOnClose,
  restoreFocusRef,
  setOpen,
  triggerRootRef,
  updatePosition,
}: UseMenuLifecycleArgs): void {
  React.useEffect(() => {
    if (!isOpen) return;

    // Focus the first enabled item when the DS owns focus-on-open: either the
    // consumer opted in via `autoFocus`, or this open was keyboard-triggered.
    if (autoFocus || focusFirstOnOpenRef.current) {
      focusFirstItem(menuRef);
    }
    focusFirstOnOpenRef.current = false;

    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false, true);
        focusTrigger(triggerRootRef);
      } else if (e.key === 'Tab') {
        // Tab out of an open menu closes it and returns focus to the trigger,
        // rather than tabbing into the portal-rendered surface.
        e.preventDefault();
        setOpen(false, false);
        focusTrigger(triggerRootRef);
      } else if (isMenuNavigationKey(e.key)) {
        e.preventDefault();
        focusByNavigationKey(e.key, menuRef);
      }
      // Enter/Space activation is handled by the focused menuitem's own onKeyDown
      // (FmMenuSurface.handleItemKeyDown). Handling it here too would double-fire
      // the item's click handler.
    };

    const handleDocumentMouseDown = (e: MouseEvent) => {
      const menu = menuRef.current;
      const trigger = triggerRootRef.current?.querySelector('[aria-haspopup="menu"]');
      const target = e.target as Node;

      if (!menu?.contains(target) && !trigger?.contains(target)) {
        setOpen(false, false);
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    document.addEventListener('mousedown', handleDocumentMouseDown);
    updatePosition();

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [isOpen, autoFocus, focusFirstOnOpenRef, menuRef, setOpen, triggerRootRef, updatePosition]);

  React.useEffect(() => {
    if (!isOpen && restoreFocusOnClose && restoreFocusRef.current) {
      restoreFocusRef.current.focus();
    }
  }, [isOpen, restoreFocusOnClose, restoreFocusRef]);
}
